import React, { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
// import { Label } from "@/components/ui/label"; // Removendo import não utilizado
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, 
  Bot, 
  Upload, 
  X, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  Copy,
  Download,
  FileJson,
  DollarSign,
  UserCog
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { uploadFile } from "@/integrations/Core";

// Função para converter valores numéricos em formato brasileiro para float
const convertToFloat = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  
  // Se for string, tentar converter
  if (typeof value === 'string') {
    // Log detalhado da conversão
    console.log(`Convertendo valor: "${value}" (${typeof value})`);
    
    // Remover pontos de separação de milhar e substituir vírgula por ponto
    const cleanValue = value.replace(/\\./g, '').replace(',', '.');
    console.log(`Valor limpo: "${cleanValue}"`);
    
    const floatValue = parseFloat(cleanValue);
    console.log(`Valor convertido: ${floatValue} (${typeof floatValue})`);
    
    if (isNaN(floatValue)) {
      console.warn(`Não foi possível converter "${value}" para número`);
      return null;
    }
    
    return floatValue;
  }
  
  return null;
};

// Função para preparar os dados para o agente
const prepareDataForAgent = (data) => {
  // Clonar os dados para não modificar o original
  const clonedData = JSON.parse(JSON.stringify(data));
  
  // Log do início da preparação dos dados
  console.log('Iniciando preparação dos dados para o agente...');
  
  // Processar os rendimentos para cada mês
  if (clonedData.rendimentos) {
    const meses = Object.keys(clonedData.rendimentos);
    
    meses.forEach(mes => {
      if (clonedData.rendimentos[mes] && clonedData.rendimentos[mes].rendimentos) {
        console.log(`Processando rendimentos do mês ${mes}...`);
        
        clonedData.rendimentos[mes].rendimentos.forEach((item, index) => {
          const originalValor = item.valor;
          item.valor = convertToFloat(item.valor);
          
          console.log(`Rendimento ${index + 1} (CPF: ${item.cpf}): 
            Valor: ${originalValor} -> ${item.valor}`);
        });
      }
    });
  }
  
  // Log da preparação concluída
  console.log('Preparação dos dados concluída.');
  
  return clonedData;
};

export default function AgentRendPF() {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Função para salvar log
  const saveLog = async (action, data = {}, response = null, error = null) => {
    try {
      const timestamp = new Date().toISOString();
      const logEntry = {
        timestamp,
        action,
        data,
        response,
        error: error ? error.toString() : null
      };
      
      // Enviar log para o servidor
      const logData = {
        type: "DEBUG",
        message: `${action}: ${JSON.stringify(data).substring(0, 100)}...`,
        data: {
          action,
          details: data,
          response: response ? response : null,
          error: error ? error.toString() : null,
          timestamp
        }
      };
      
      console.log("Enviando log para servidor:", logData);
      
      // Fazer requisição para o endpoint de log
      try {
        const logResponse = await fetch('/api/log.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(logData)
        });
        
        if (!logResponse.ok) {
          console.error('Erro ao salvar log no servidor:', await logResponse.text());
        } else {
          console.log(`Log salvo com sucesso: ${action}`);
        }
      } catch (serverLogError) {
        console.error('Exceção ao tentar salvar log no servidor:', serverLogError);
      }
    } catch (logError) {
      console.error('Erro ao preparar log:', logError);
    }
  };

  // Função para salvar log detalhado em arquivo específico
  const saveDetailedLog = async (action, data, response = null, error = null) => {
    try {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0]; // formato YYYY-MM-DD
      const timestamp = today.toISOString();
      
      const logEntry = {
        timestamp,
        action,
        data,
        response,
        error: error ? error.toString() : null
      };
      
      // Criar o nome do arquivo de log com a data atual
      const fileName = `Log-AgentRendPF-${dateStr}.json`;
      
      // Preparar o conteúdo do log
      const logContent = JSON.stringify(logEntry, null, 2);
      
      // Criar um blob com o conteúdo do log
      const blob = new Blob([logContent], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file', blob, fileName);
      formData.append('append', 'true'); // Indicar que queremos anexar ao arquivo existente
      
      // Enviar o log para o servidor
      const saveResponse = await fetch('/api/save_log.php', {
        method: 'POST',
        body: formData
      });
      
      if (!saveResponse.ok) {
        console.error(`Erro ao salvar log detalhado: ${saveResponse.statusText}`);
      } else {
        console.log(`Log detalhado salvo com sucesso: ${fileName}`);
      }
    } catch (logError) {
      console.error('Erro ao salvar log detalhado:', logError);
    }
  };

  // Função para lidar com eventos de arrastar arquivos
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Função para lidar com soltar arquivos
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  // Função para lidar com seleção de arquivos pelo input
  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  // Função para abrir o seletor de arquivos
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Função para remover um arquivo
  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // Função para formatar o tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  // Função para processar o arquivo JSON e enviar para o agente
  const processFile = async () => {
    if (files.length === 0) {
      setError("Selecione um arquivo JSON para processar");
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      // Registrar início do processamento
      await saveLog('INICIO_PROCESSAMENTO', { 
        filesCount: files.length, 
      });
      
      // Log detalhado do início do processamento
      await saveDetailedLog('INICIO_PROCESSAMENTO', { 
        filesCount: files.length, 
        fileNames: files.map(f => f.name),
      });

      // Ler o conteúdo do arquivo
      const file = files[0]; // Pegamos apenas o primeiro arquivo
      
      // Criar uma Promise para aguardar a leitura do arquivo
      const readFilePromise = new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsText(file);
      });
      
      // Aguardar a leitura do arquivo
      const fileText = await readFilePromise;
      
      // Tentar fazer o parse do JSON
      let rendimentosData;
      try {
        rendimentosData = JSON.parse(fileText);
        await saveLog('ARQUIVO_LIDO', { 
          fileName: file.name,
          tamanho: fileText.length
        });
      } catch (parseError) {
        await saveLog('ERRO_PARSE_JSON', { fileName: file.name }, null, parseError);
        throw new Error(`Erro ao analisar o JSON do arquivo ${file.name}: ${parseError.message}`);
      }
      
      // Verificar se o arquivo tem a estrutura esperada
      if (!rendimentosData.rendimentos) {
        throw new Error(`Arquivo ${file.name} não tem a estrutura esperada para Rendimentos Pessoa Física`);
      }
      
      // Converter valores numéricos antes de enviar ao agente
      const dataForAgent = prepareDataForAgent(rendimentosData);
      
      // Log dos dados preparados para o agente
      await saveLog('DADOS_PREPARADOS_PARA_AGENTE', { 
        dadosOriginais: {
          amostra: JSON.stringify(rendimentosData).substring(0, 100) + '...'
        },
        dadosConvertidos: {
          amostra: JSON.stringify(dataForAgent).substring(0, 100) + '...'
        }
      });
      
      // Log do resultado do processamento
      await saveLog('PROCESSAMENTO_CONCLUIDO', { dataForAgent });
      
      // Log detalhado do resultado do processamento
      await saveDetailedLog('PROCESSAMENTO_CONCLUIDO', { 
        dataForAgent,
        timestamp: new Date().toISOString(),
        filesProcessed: files.map(f => f.name)
      });
      
      // Enviar os dados para o agente
      try {
        await saveLog('ENVIANDO_PARA_AGENTE', { url: 'http://host-lenovo.ddns.net:8000/IRPF/Rend-PF' });
        
        // Salvar os dados processados em um arquivo de log para referência
        await saveDetailedLog('DADOS_ENVIADOS_PARA_AGENTE', { 
          dataForAgent: JSON.parse(JSON.stringify(dataForAgent)),
          timestamp: new Date().toISOString()
        });
        
        // Log detalhado dos dados enviados para o agente
        await saveDetailedLog('ENVIANDO_PARA_AGENTE', { 
          url: 'http://host-lenovo.ddns.net:8000/IRPF/Rend-PF',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer fastapi_live_f6d0d199fa86abaa4d1169983c5f3f66'
          },
          data: dataForAgent,
          timestamp: new Date().toISOString()
        });
        
        // Atualizar mensagem de processamento
        setProcessing(true);
        setError(null);
        setResult({
          message: "Enviando dados para o agente...",
          status: "sending",
          timestamp: new Date().toISOString()
        });
        
        // Salvar os dados processados em um arquivo JSON para referência
        try {
          const agentDataBlob = new Blob([JSON.stringify(dataForAgent, null, 2)], { type: 'application/json' });
          const formData = new FormData();
          formData.append('file', agentDataBlob, 'AgentRendPF.json');
          
          const saveResponse = await fetch('/api/save_log.php', {
            method: 'POST',
            body: formData
          });
          
          if (saveResponse.ok) {
            console.log('Dados do agente salvos em arquivo para referência');
            await saveLog('DADOS_AGENTE_SALVOS', { 
              arquivo: 'AgentRendPF.json',
              tamanho: JSON.stringify(dataForAgent).length
            });
          }
        } catch (saveError) {
          console.warn('Erro ao salvar dados do agente em arquivo:', saveError);
        }
        
        // Log de debug antes de enviar para o proxy
        console.log('DEBUG - Enviando dados para o proxy:', {
          url: '/api/agent_proxy.php',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          dataSize: JSON.stringify(dataForAgent).length,
          dataPreview: JSON.stringify(dataForAgent).substring(0, 500) + '...'
        });
        
        await saveLog('ENVIANDO_PARA_PROXY', { 
          url: '/api/agent_proxy.php',
          dataSize: JSON.stringify(dataForAgent).length,
          dataPreview: JSON.stringify(dataForAgent).substring(0, 200) + '...'
        });
        
        // Usar o proxy PHP que utiliza cURL para fazer a requisição ao endpoint correto
        const agentResponse = await fetch('/api/agent_proxy.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dataForAgent)
        });
        
        // Log de debug após receber resposta do proxy
        console.log('DEBUG - Resposta do proxy:', {
          status: agentResponse.status,
          statusText: agentResponse.statusText,
          headers: Object.fromEntries([...agentResponse.headers.entries()])
        });
        
        await saveLog('RESPOSTA_DO_PROXY', { 
          status: agentResponse.status,
          statusText: agentResponse.statusText,
          headers: Object.fromEntries([...agentResponse.headers.entries()])
        });
        
        // Obter o texto da resposta para log
        const responseText = await agentResponse.text();
        console.log('DEBUG - Texto da resposta:', responseText.substring(0, 500));
        
        await saveLog('TEXTO_RESPOSTA_PROXY', { 
          responsePreview: responseText.substring(0, 500)
        });
        
        let agentResult;
        
        try {
          // Tentar fazer parse do JSON da resposta
          agentResult = JSON.parse(responseText);
          console.log('DEBUG - JSON da resposta:', agentResult);
          
          await saveLog('JSON_RESPOSTA_PROXY', { 
            parsedJson: agentResult
          });
        } catch (e) {
          // Se não for um JSON válido, usar o texto como está
          agentResult = { response: responseText };
          console.error('DEBUG - Erro ao fazer parse do JSON:', e);
          
          await saveLog('ERRO_PARSE_JSON', { 
            error: e.toString(),
            responseText: responseText.substring(0, 500)
          });
        }
        
        if (!agentResponse.ok) {
          console.error('DEBUG - Resposta não ok:', {
            status: agentResponse.status,
            statusText: agentResponse.statusText,
            responseText: responseText.substring(0, 500)
          });
          
          await saveLog('RESPOSTA_NAO_OK', { 
            status: agentResponse.status,
            statusText: agentResponse.statusText,
            responseText: responseText.substring(0, 500)
          });
          
          throw new Error(`Erro ao enviar dados para o agente: ${agentResponse.status} - ${responseText}`);
        }
        
        // Log da resposta do agente
        await saveLog('RESPOSTA_AGENTE_SUCESSO', { 
          status: agentResponse.status,
          agentResult 
        });
        
        // Log detalhado da resposta do agente
        await saveDetailedLog('RESPOSTA_AGENTE_SUCESSO', { 
          status: agentResponse.status,
          statusText: agentResponse.statusText,
          headers: Object.fromEntries([...agentResponse.headers.entries()]),
          agentResult,
          timestamp: new Date().toISOString()
        });
        
        // Se a resposta for com código 200, mostrar apenas "Task Started"
        if (agentResponse.status === 200) {
          setResult({
            message: "Task Started",
            status: "success",
            timestamp: new Date().toISOString(),
            processedData: dataForAgent
          });
        } else {
          setResult({
            message: "Dados enviados com sucesso para o agente!",
            status: "success",
            timestamp: new Date().toISOString(),
            agentResponse: agentResult,
            processedData: dataForAgent
          });
        }
        
      } catch (agentError) {
        await saveLog('ERRO_ENVIO_AGENTE', {}, null, agentError);
        await saveDetailedLog('ERRO_ENVIO_AGENTE', {
          error: agentError.toString(),
          stack: agentError.stack,
          timestamp: new Date().toISOString()
        });
        console.error("Erro ao enviar dados para o agente:", agentError);
        setError(`Erro ao enviar dados para o agente: ${agentError.message}`);
      }
      
    } catch (error) {
      // Log do erro geral
      await saveLog('ERRO_PROCESSAMENTO', {}, null, error);
      await saveDetailedLog('ERRO_PROCESSAMENTO', {
        error: error.toString(),
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      console.error("Erro ao processar arquivos:", error);
      setError(`Erro ao processar arquivos: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Função para copiar o JSON para a área de transferência
  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(JSON.stringify(result.processedData || result, null, 2))
        .then(() => {
          alert("JSON copiado para a área de transferência!");
        })
        .catch(err => {
          console.error("Erro ao copiar para a área de transferência:", err);
          alert("Erro ao copiar para a área de transferência");
        });
    }
  };

  // Função para baixar o JSON
  const downloadJson = () => {
    if (result) {
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(result.processedData || result, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", "AgentRendPF.json");
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Cabeçalho */}
      <header className="p-4 border-b bg-white shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => window.history.back()}
              className="mr-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-semibold flex items-center">
              <UserCog className="h-8 w-8 text-blue-600 mr-2" />
              Agent Rendimento Pessoa Física
            </h1>
          </div>
        </div>
      </header>

      {/* Conteúdo principal */}
      <main className="flex-1 p-4 container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna da esquerda */}
          <div className="lg:col-span-2 space-y-6">
            {/* Card de upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Upload de Arquivos JSON</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 transition-colors flex flex-col items-center justify-center relative ${
                    dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".json"
                    onChange={handleFileInput}
                    multiple
                  />
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                    <Upload className="h-6 w-6 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Arraste seus arquivos JSON aqui</h3>
                  <p className="text-sm text-gray-500 text-center mb-4">
                    ou clique para selecionar arquivos do seu computador
                  </p>
                  <Button
                    type="button"
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={handleButtonClick}
                  >
                    Selecionar Arquivos
                  </Button>
                </div>
                
                {/* Lista de arquivos selecionados */}
                {files.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium mb-2">Arquivos selecionados:</h3>
                    <div className="space-y-2">
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between border rounded-lg p-2 text-sm"
                        >
                          <div className="flex items-center">
                            <FileJson className="h-4 w-4 text-blue-500 mr-2" />
                            <span className="truncate max-w-xs">{file.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{formatFileSize(file.size)}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeFile(index)}
                              disabled={uploading || processing}
                              className="h-6 w-6"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Botão de processamento */}
                <div className="mt-4 flex justify-end">
                  <Button
                    type="button"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={processFile}
                    disabled={files.length === 0 || processing}
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <Bot className="h-4 w-4 mr-2" />
                        Start Agent
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Mensagens de erro */}
                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">{error}</div>
                  </div>
                )}
                
                {/* Resultados */}
                {result && (
                  <div className="mt-4">
                    <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-teal-800">
                        <p className="font-medium">{result.message}</p>
                        <p className="text-xs text-teal-600 mt-1">
                          {new Date(result.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Botões de ação para o resultado */}
                    <div className="mt-4 flex gap-2 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        className="text-gray-700"
                        onClick={copyToClipboard}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copiar JSON
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="text-gray-700"
                        onClick={downloadJson}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Baixar JSON
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Coluna da direita */}
          <div className="space-y-6">
            {/* Card de informações */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sobre o Agent Rendimento PF</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Assistente Inteligente</h3>
                      <p className="text-sm text-gray-500">
                        O Agent Rendimento PF automatiza tarefas relacionadas ao processamento de rendimentos de pessoa física.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <FileJson className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Processamento de JSON</h3>
                      <p className="text-sm text-gray-500">
                        Faça upload de arquivos JSON com dados de rendimentos organizados por mês.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Rendimentos Mensais</h3>
                      <p className="text-sm text-gray-500">
                        Processamento de rendimentos para todos os meses do ano com valores por CPF.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h3 className="font-medium text-gray-900 mb-2">Como usar</h3>
                  <ol className="text-sm text-gray-500 space-y-2 list-decimal list-inside">
                    <li>Faça upload do arquivo JSON com os dados de rendimentos</li>
                    <li>Clique em "Start Agent" para iniciar o processamento</li>
                    <li>Aguarde enquanto os dados são enviados para o agente</li>
                    <li>Visualize a resposta do agente e, se necessário, baixe o JSON processado</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            {/* Card de status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Status do Processamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Arquivos selecionados:</span>
                    <span className="text-sm">{files.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Status:</span>
                    <span className="text-sm">
                      {processing ? (
                        <span className="text-amber-600">Processando...</span>
                      ) : result && result.status === "success" ? (
                        <span className="text-green-600">Concluído</span>
                      ) : result && result.status === "sending" ? (
                        <span className="text-blue-600">Enviando...</span>
                      ) : error ? (
                        <span className="text-red-600">Erro</span>
                      ) : (
                        <span className="text-gray-500">Aguardando</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Endpoint do Agente:</span>
                    <span className="text-sm text-gray-500 truncate max-w-[180px]">host-lenovo.ddns.net:8000/IRPF/Rend-PF</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
